import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import formatPrice from "@/core/helpers/formatPrice";
import hexToRgb from "@/core/helpers/hexToRgb";
import { PinIcon, Star3Icon } from "@/shared/icons";
import { Button } from "@/shared/ui";
import { FactionEnum } from "@/domains/faction/constants/factionEnum";
import { factionsList } from "@/domains/faction/helpers/factionsList";
import FactionProgressLine from "@/domains/faction/components/FactionProgressLine";
import FactionTextBlock from "@/domains/faction/components/FactionTextBlock";

type Props = {
  title: string;
  status: string;
  description: string;
  currentCount: number;
  maxCount: number;
  url: string;
  faction: FactionEnum;
  isComplete?: boolean;
  isNoReward?: boolean;
};

export default function FactionTaskCard({
  title,
  status,
  description,
  currentCount,
  maxCount,
  url,
  faction,
  isComplete,
  isNoReward,
}: Props) {
  const { t } = useTranslation("faction");

  const [r, g, b] = hexToRgb(factionsList[faction]?.color);

  const progress = useMemo(
    () => Math.max(0, Math.min(100, (currentCount / Math.max(1, maxCount)) * 100)),
    [currentCount, maxCount],
  );

  return (
    <div>
      <div
        className="relative py-4.5 px-4 bg-dark-800 rounded-t-2xl"
        style={{
          background: `linear-gradient(312deg, rgba(${r}, ${g}, ${b}, 0.00) 45.73%, rgba(${r}, ${g}, ${b}, 0.03) 83.66%, rgba(${r}, ${g}, ${b}, 0.12) 100%), #181818`,
        }}
      >
        <div className="flex items-center mb-4">
          <p className="text-17 font-bold leading-none">{title}</p>
          <FactionTextBlock
            text={status}
            className="h-auto ml-auto p-1.5 bg-[#212121] rounded-[0.3125rem]"
            classNameText1="text-11 text-white/70"
          />
        </div>

        <p className="mb-4 text-14 font-medium text-white/70">{description}</p>

        <div className="flex items-end mb-3">
          <p className="text-12 font-bold text-white/70 leading-none uppercase">
            {t("faction.progress")}:
          </p>
          <div className="flex items-end gap-1 ml-auto">
            <p className="text-16 font-medium leading-none">{currentCount}</p>
            <p className="text-13 font-medium text-[#B8B8B8] leading-none">/{maxCount}</p>
          </div>
        </div>

        <FactionProgressLine progress={progress} />

        {isComplete && (
          <div
            className="absolute inset-0 rounded-t-2xl flex justify-center items-center"
            style={{
              background: isNoReward
                ? `
                radial-gradient(50% 57.35% at 50% 100%, rgba(${r}, ${g}, ${b}, 0.30) 0%, rgba(${r}, ${g}, ${b}, 0.00) 100%),
                rgba(0, 0, 0, 0.80)
              `
                : `
                radial-gradient(50% 57.35% at 50% 100%, rgba(${r}, ${g}, ${b}, 0.20) 0%, rgba(${r}, ${g}, ${b}, 0.00) 100%),
                linear-gradient(180deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.10) 100%)
              `,
              backdropFilter: "blur(4px)",
            }}
          >
            <div className="text-center">
              <div className="mb-4 text-16 font-bold uppercase">{t("faction.taskComplete")}</div>
              <Button
                className={clsx(
                  "h-11 py-4 px-7.5",
                  isNoReward && "bg-[#1D1D1D]/90 pointer-events-none",
                )}
                variant={isNoReward ? "solid" : "solid-gradient"}
              >
                {isNoReward ? t("faction.rewardReceived") : t("faction.takeReward")}
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="p-3.5 bg-[#1C1C1C] rounded-b-2xl">
        <p className="mb-2.5 text-12 font-bold text-white/70 leading-none uppercase">
          {t("faction.reward")}:
        </p>
        <div className="flex items-end">
          <div
            className="relative flex items-end size-12.5 p-1 rounded-lg"
            style={{
              background:
                "linear-gradient(180deg, rgba(24, 24, 24, 0.00) 25%, rgba(255, 87, 15, 0.10) 100%), rgba(33, 33, 33, 0.58)",
            }}
          >
            <div
              className="absolute top-1/2 left-1/2 size-7 bg-contain bg-no-repeat bg-center -translate-x-1/2 -translate-y-1/2"
              style={{ backgroundImage: `url(${url})` }}
            />
            <div
              className="absolute inset-0 rounded-[inherit]"
              style={{
                padding: "2px",
                background: `linear-gradient(90deg, transparent, #FF845040 25%, #FF8450 50%, #FF845040 75%, transparent)`,
                WebkitMask: `
                  linear-gradient(#fff 0 0) content-box, 
                  linear-gradient(#fff 0 0)
                `,
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
                clipPath: "inset(calc(100% - 4px) 0 0 0)",
                zIndex: 0,
              }}
            />
            <p className="relative z-1 text-11 font-bold">456</p>
          </div>

          <div className="flex flex-col justify-between h-12.5 ml-2">
            <div className="flex justify-center items-center h-5.5 p-1 text-12 font-bold text-[#B25BFF] leading-none uppercase bg-[#B25BFF21] rounded">
              <Star3Icon className="size-3.5" />
              <p className="ml-2.5">
                {formatPrice(345)} {t("faction.of")}
              </p>
            </div>
            <div className="flex justify-center items-center h-5.5 p-1 text-12 font-bold text-accent-solid leading-none uppercase bg-accent-solid/12 rounded">
              {formatPrice(150000)} ₽
            </div>
          </div>

          <button
            type="button"
            className="relative flex justify-center items-center size-8.5 ml-auto bg-white/3 rounded-xl overflow-clip"
          >
            <div
              className={cn(
                "absolute top-0 left-0 size-full opacity-0 transition-all duration-200 hover:opacity-100",
                factionsList[faction]?.gradient,
              )}
            />
            <PinIcon className="relative z-1 size-3.5 pointer-events-none" />
          </button>
        </div>
      </div>
    </div>
  );
}
